import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaQuoteLeft, FaCheckCircle, FaLightbulb, FaCogs, FaRocket } from 'react-icons/fa';
import './Purpose.css';

const Purpose = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="purpose-page">
            {/* Purpose Hero Section */}
            <section className="purpose-hero section" data-aos="fade-up">
                <div className="container">
                    <div className="purpose-hero-content">
                        <h1>Our Purpose <span className="text-gradient">& Philosophy</span> </h1>
                    </div>
                </div>
            </section>

            {/* Why Purpose Matters - Left Right Layout */}
            <section className="section why-section" data-aos="fade-up">
                <div className="container">
                    <div className="why-content">
                        <div className="why-text">
                            <h3 style={{ fontSize: '1rem', textTransform: 'uppercase', color: 'var(--primary-color)', marginBottom: '0.5rem', fontWeight: '700' }}>PURPOSE & PHILOSOPHY</h3>
                            <h2 style={{ fontSize: '2.5rem', fontWeight: 900, color: 'var(--text-primary)', marginBottom: 'var(--spacing-md)', lineHeight: 1.2 }}>Why Purpose Matters to Us</h2>
                            <p className="why-intro">
                                At Triology, we believe organizations do not transform through tools, frameworks, or effort alone. They evolve when clarity, leadership intent, systems, and execution discipline come into alignment.
                            </p>
                            <div className="quote-box" style={{ marginTop: '2rem', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderLeft: '4px solid var(--primary-color)', padding: '1.5rem', borderRadius: '12px' }}>
                                <FaQuoteLeft className="quote-icon" />
                                <p style={{ fontSize: '0.95rem', fontStyle: 'italic', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.8 }}>
                                    Our purpose and philosophy guide how we think, how we engage, and how we partner with organizations. They are not statements on a wall they shape the way we consult.
                                </p>
                            </div>
                        </div>
                        <div className="why-image">
                            <img src="/assets/images/img-3.jpg" alt="Purpose Philosophy" />
                        </div>
                    </div>
                </div>
            </section>

            <section id="purpose" className="section process-section" data-aos="fade-up">
                <div className="container">
                    <div className="beliefs-header" style={{ textAlign: 'center', marginBottom: '3rem' }}>
                        <h3 style={{ fontSize: '0.9rem', textTransform: 'uppercase', color: 'var(--primary-color)', marginBottom: '1rem', fontWeight: '800' }}>OUR PURPOSE</h3>
                        <h2 style={{ maxWidth: '900px', margin: '0 auto 2rem' }}>We Serve as a Catalyst in the Creation of Highly Evolved Organizations</h2>
                        <div className="purpose-quote-wrapper">
                            <p className="purpose-main-quote">
                                "Transformation, in our view, must be owned internally and sustained beyond the engagement."
                            </p>
                        </div>
                    </div>

                    <div className="purpose-details-grid">
                        <div className="purpose-detail-col">
                            <p>
                                Triology exists to serve as a catalyst in the creation of highly evolved organizations organizations that operate with clarity, strong leadership, disciplined systems, and sustainable execution.
                            </p>
                        </div>
                        <div className="purpose-detail-col">
                            <p>
                                As consultants, our role is not to own change, but to accelerate it. We enable organizations to evolve by helping leadership teams build the clarity, structures, and execution discipline required to thrive in complexity.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Catalyst Definition */}
            <section className="section" data-aos="fade-up">
                <div className="container">
                    <div className="beliefs-header" style={{ textAlign: 'center' }}>
                        <h3 style={{ fontSize: '1rem', textTransform: 'uppercase', color: 'var(--primary-color)', marginBottom: '0.5rem', fontWeight: '700' }}>WHAT "CATALYST" MEANS TO US</h3>
                        <h2>Accelerating Evolution Without Creating Dependency</h2>
                    </div>
                    <p className="intro-text centered">Being a catalyst means </p>

                    <div className="catalyst-grid">
                        <div className="catalyst-card">
                            <div className="catalyst-icon">
                                <FaCheckCircle />
                            </div>
                            <p>We enable momentum without becoming a crutch</p>
                        </div>
                        <div className="catalyst-card">
                            <div className="catalyst-icon">
                                <FaCheckCircle />
                            </div>
                            <p>We strengthen systems rather than replace them</p>
                        </div>
                        <div className="catalyst-card">
                            <div className="catalyst-icon">
                                <FaCheckCircle />
                            </div>
                            <p>We build internal capability rather than external dependence</p>
                        </div>
                    </div>

                    <p className="catalyst-footer centered">
                        Triology works alongside leadership teams to activate clarity and discipline, while ensuring ownership remains firmly within the organization.
                    </p>
                </div>
            </section>

            {/* Our Philosophy */}
            <section id="philosophy" className="section philosophy-beliefs-section" data-aos="fade-up">
                <div className="container">
                    <div className="beliefs-header" style={{ textAlign: 'center', marginBottom: '2rem' }}>
                        <h3 style={{ fontSize: '1rem', textTransform: 'uppercase', color: 'var(--primary-color)', marginBottom: '0.5rem', fontWeight: '700' }}>OUR PHILOSOPHY</h3>
                    </div>
                    <div className="philosophy-content">
                        <div className="philosophy-sections-wrapper">
                            {/* Left Side - Beliefs */}
                            <div className="beliefs-section-left">
                                <h2 className="left-section-title">How We Think About Organizations and Change</h2>
                                <p className="left-section-intro">
                                    Our consulting philosophy is grounded in a few core beliefs:
                                </p>
                                <div className="beliefs-grid">
                                    <div className="belief-card">
                                        <div className="belief-number">01</div>
                                        <p>Organizations are living systems, not isolated functions.</p>
                                    </div>
                                    <div className="belief-card">
                                        <div className="belief-number">02</div>
                                        <p>Clarity precedes performance.</p>
                                    </div>
                                    <div className="belief-card">
                                        <div className="belief-number">03</div>
                                        <p>Systems matter more than individual heroics.</p>
                                    </div>
                                    <div className="belief-card">
                                        <div className="belief-number">04</div>
                                        <p>Execution is a discipline, not an event.</p>
                                    </div>
                                </div>
                                <p className="belief-footer">
                                    These beliefs shape every engagement we undertake.
                                </p>
                            </div>

                            {/* Right Side - Principles */}
                            <div className="principles-section-right">
                                <h3 className="subsection-title">Clarity Before Action. Systems Before Scale. Execution With Ownership.</h3>
                                <p className="subsection-intro">
                                    At Triology, we do not believe in quick fixes or one-size-fits-all frameworks. Our consulting philosophy is anchored in three principles:
                                </p>

                                <div className="principles-grid">
                                    <div className="principle-card">
                                        <div className="principle-icon">
                                            <FaLightbulb />
                                        </div>
                                        <div>
                                            <h4>Diagnosis before solutions</h4>
                                            <p>We seek to understand root causes before recommending interventions.</p>
                                        </div>
                                    </div>
                                    <div className="principle-card">
                                        <div className="principle-icon">
                                            <FaCogs />
                                        </div>
                                        <div>
                                            <h4>System thinking over isolated actions</h4>
                                            <p>We design structures, processes, and governance that work together.</p>
                                        </div>
                                    </div>
                                    <div className="principle-card">
                                        <div className="principle-icon">
                                            <FaRocket />
                                        </div>
                                        <div>
                                            <h4>Execution as a capability</h4>
                                            <p>Execution is not an event it is a discipline that must be built into the organization.</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="philosophy-conclusion">
                                    <p>This philosophy ensures that change is sustainable and owned internally.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="section bg-white" data-aos="fade-up">
                <div className="container">
                    <div className="philosophy-action-header">
                        <h3 className="section-label-centered">HOW THIS PHILOSOPHY SHOWS UP</h3>
                        <h2 className="section-title-centered">Our Philosophy in Action</h2>
                    </div>

                    <div className="philosophy-action-content">
                        <div className="action-list-col">
                            <p className="action-intro-text">
                                Triology's philosophy translates into action through the way we consult:
                            </p>
                            <ul className="action-bullets">
                                <li>We diagnose before we design</li>
                                <li>We design systems before pushing execution</li>
                                <li>We embed governance before scaling initiatives</li>
                            </ul>
                        </div>
                        <div className="action-cosmos-col">
                            <div className="gray-action-card">
                                <div className="cosmos-integration-text">
                                    <p>
                                        This philosophy is reflected in our <strong>Diagnose–Design–Deliver</strong> approach and is governed through <strong>COSMOS</strong>, our unified execution operating system.
                                    </p>
                                    <p className="action-partnership-text" style={{ marginBottom: 0 }}>
                                        We partner deeply, but ensure ownership stays internal.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Growth View - Left Right Layout */}
            {/* Growth View - Wrapper Rewrite */}
            <section className="gv-section" data-aos="fade-up">
                <div className="container">
                    <div className="gv-header">
                        <span className="gv-subtitle">OUR VIEW ON GROWTH</span>
                        <h3 className="gv-title">Growth With Readiness, Not Acceleration Alone</h3>
                    </div>

                    <div className="gv-content">
                        <div className="gv-card-box">
                            <div className="gv-card">
                                <p>We believe growth without readiness creates fragility.</p>
                            </div>
                        </div>
                        <div className="gv-text-box">
                            <p className="gv-text-p">
                                Highly evolved organizations scale consciously with aligned leadership, clear systems, and disciplined execution.
                            </p>
                            <p className="gv-text-p">
                                Our work helps organizations pause, reflect, and realign so that growth becomes sustainable rather than stressful.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer Anchor */}
            <section className="building-org-section section" data-aos="fade-up">
                <div className="container">
                    <div className="building-card">
                        <h2 className="building-title">Purpose as a Quiet Anchor</h2>
                        <p className="building-text">
                            Triology's purpose and philosophy are not meant to impress they are meant to guide. They ensure that our work remains grounded, responsible, and focused on creating lasting value for the organizations we partner with.
                        </p>
                        <span className="highlight-text">
                            "When purpose guides consulting, execution gains meaning."
                        </span>
                        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                            <Link to="/contact" className="cta-button">
                                Start Your Journey →
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Purpose;
